import { Box, SelectField, Text } from '@factorialco/deprecated-design-system'

import {
  CheckSubscriptionItemsInstalledDocument,
  NextInvoiceDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import PriceInfo from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/PriceInfo'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  formatDate,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

const FREE_PLAN_VALUE = '__free__'

const ONE_TIER_RANK: Record<string, number> = {
  'one starter': 0,
  'one growth': 1,
  'one mid': 2,
  'one scale': 3,
}

type OneVariantSelectorProps = {
  addon: Plan
  crossSellingAddon?: Plan | undefined
  showPrice: boolean
  setSelectedAddonId: (id: string) => void
  installedAddonInternalId?: string | null
}

const getDefaultVariant = (plan: Plan): VariantsEnum | undefined => {
  if (plan.variantTypes.includes(ENTERPRISE_VARIANT)) return ENTERPRISE_VARIANT
  if (plan.variantTypes.includes(BUSINESS_VARIANT)) return BUSINESS_VARIANT
  return undefined
}

const getOneTierRank = (internalTitle: string): number => {
  const titleLower = internalTitle.toLowerCase()
  for (const [key, rank] of Object.entries(ONE_TIER_RANK)) {
    if (titleLower.includes(key)) return rank
  }
  return -1
}

const matchesInstalledAddonId = (installedAddonId: string, planIds: string[]): boolean => {
  const normalizedInstalledAddonId = installedAddonId.toLowerCase().trim()

  return planIds.some((planId) => {
    const normalizedPlanId = planId.toLowerCase().trim()
    return (
      normalizedPlanId === normalizedInstalledAddonId ||
      normalizedPlanId.includes(normalizedInstalledAddonId) ||
      normalizedInstalledAddonId.includes(normalizedPlanId)
    )
  })
}

const OneVariantSelector = ({
  addon,
  crossSellingAddon,
  showPrice,
  setSelectedAddonId,
  installedAddonInternalId,
}: OneVariantSelectorProps) => {
  const {
    allAddons,
    configPlan,
    setConfigPlan,
    period,
    togglePlanSelect,
    currency,
    discount,
    seats,
    currentSeats,
  } = useSelfUpsellContext()

  const { company } = useCurrentSession()

  const onePlans = allAddons.f25Addons

  const installedOnePlan = onePlans.find((plan) => {
    if (!installedAddonInternalId) return false

    const planIds = [plan.pricing.getPlanId(period), ...plan.pricing.getAllPlanIds(period)].filter(
      (id): id is string => Boolean(id)
    )

    return matchesInstalledAddonId(installedAddonInternalId, planIds)
  })

  const { data: subscriptionItemsData } = useQuery(CheckSubscriptionItemsInstalledDocument, {
    variables: {},
  })

  const { data: nextInvoiceData } = useQuery(NextInvoiceDocument, {
    variables: { companyId: company.id },
  })

  const nextBillingDate = (() => {
    const nextBillingAt =
      nextInvoiceData?.billing?.cpNextBillingEstimatesConnection?.nodes[0]?.subscriptionEstimate
        ?.nextBillingAt
    if (!nextBillingAt) return null
    return formatDate(new Date(nextBillingAt * 1000))
  })()

  const installedOnePlans = (() => {
    const items = subscriptionItemsData?.billing?.subscriptionItemsConnection?.nodes || []
    const allItemIds = items.flatMap((item) => item.itemIds || [])
    if (allItemIds.length === 0) return installedOnePlan ? [installedOnePlan] : []

    const matched: Plan[] = []
    for (const plan of onePlans) {
      const planIds = [
        plan.pricing.getPlanId(period),
        ...plan.pricing.getAllPlanIds(period),
      ].filter((id): id is string => Boolean(id))

      const isInSubscription = allItemIds.some((subId) => matchesInstalledAddonId(subId, planIds))
      if (isInSubscription) matched.push(plan)
    }

    return matched.length > 0 ? matched : installedOnePlan ? [installedOnePlan] : []
  })()

  const maxInstalledOnePlan = (() => {
    if (installedOnePlans.length === 0) return undefined
    return installedOnePlans.reduce((max, plan) => {
      return getOneTierRank(plan.internalTitle) > getOneTierRank(max.internalTitle) ? plan : max
    })
  })()

  const selectableOnePlans = onePlans.filter(
    (plan) => !installedOnePlans.some((installed) => installed.internalTitle === plan.internalTitle)
  )

  const getPlanLabel = (plan: Plan): { label: string; disabled: boolean } => {
    const isInstalled = installedOnePlans.some(
      (installed) => installed.internalTitle === plan.internalTitle
    )
    if (!isInstalled) return { label: plan.title, disabled: false }

    const isMaxPlan = maxInstalledOnePlan?.internalTitle === plan.internalTitle
    const hasMultipleInstalled = installedOnePlans.length > 1

    if (hasMultipleInstalled && !isMaxPlan) {
      const untilLabel = nextBillingDate
        ? i18n.t('self_upsell.first_step.in_plan_until', {
            defaultValue: 'In your plan until {{date}}',
            date: nextBillingDate,
          })
        : i18n.t('marketplace.revamp.in_your_plan')
      return { label: `${plan.title} (${untilLabel})`, disabled: true }
    }

    return {
      label: `${plan.title} (${i18n.t('marketplace.revamp.in_your_plan')})`,
      disabled: true,
    }
  }

  const oneSelectorOptions = [
    {
      label: i18n.t('self_upsell.first_step.free_option', { defaultValue: 'Free' }),
      value: FREE_PLAN_VALUE,
      disabled: false,
    },
    ...onePlans.map((plan) => {
      const { label, disabled } = getPlanLabel(plan)
      return { label, value: plan.internalTitle, disabled }
    }),
  ]

  const handleOnePlanChange = (selectedPlan: Plan) => {
    const defaultVariant = getDefaultVariant(selectedPlan)
    if (!defaultVariant) return

    if (!selectedPlan.isSelected) togglePlanSelect(selectedPlan)

    selectedPlan.setActiveVariant(defaultVariant)
    setSelectedAddonId(selectedPlan.internalTitle)

    const addons = [selectedPlan.pricing.getPlanId(period) || '']
    const crossSellingPlanId = crossSellingAddon?.pricing.getPlanId(period)
    if (crossSellingPlanId) addons.push(crossSellingPlanId)

    setConfigPlan({
      addon: addons,
      variant: defaultVariant,
    })
  }

  useEffect(() => {
    if (selectableOnePlans.length === 0) return

    const currentPlanIsInstalled = installedOnePlans.some(
      (installed) => installed.internalTitle === addon.internalTitle
    )
    if (currentPlanIsInstalled && selectableOnePlans[0]) {
      handleOnePlanChange(selectableOnePlans[0])
    }
  }, [])

  const isFreePlanSelected = configPlan.addon?.length === 0
  const selectedOneVariant = isFreePlanSelected ? undefined : getDefaultVariant(addon)

  return (
    <Box gap="s6" width="full" style={{ minWidth: '0' }}>
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('growth.self_upsell.one_selector')}
      </Text.Normal>
      <Box gap="s8" width="full" style={{ minWidth: '0' }}>
        {oneSelectorOptions.length > 0 && (
          <Box width="full" style={{ minWidth: '0', maxWidth: '100%' }}>
            <SelectField
              hideLabel
              options={oneSelectorOptions}
              value={isFreePlanSelected ? FREE_PLAN_VALUE : addon.internalTitle}
              onChange={(selectedId) => {
                if (selectedId === FREE_PLAN_VALUE) {
                  setConfigPlan({
                    addon: [],
                    variant: BUSINESS_VARIANT,
                  })
                  return
                }

                const selectedPlan = onePlans.find((plan) => plan.internalTitle === selectedId)
                if (
                  installedOnePlans.some(
                    (installed) => installed.internalTitle === selectedPlan?.internalTitle
                  )
                )
                  return
                if (!selectedPlan) return
                handleOnePlanChange(selectedPlan)
              }}
              selectValue={isFreePlanSelected ? FREE_PLAN_VALUE : addon.internalTitle}
              missingLabel={i18n.t('self_upsell.first_step.select_plan_type')}
            />
          </Box>
        )}

        {isFreePlanSelected ? (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'viridian1000',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={'viridian100'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {i18n.t('self_upsell.first_step.free_option', { defaultValue: 'Free' })}
              </Text.Normal>
            </Box>
            <Text.Normal size="200" color="grey700">
              {i18n.t('self_upsell.first_step.free_description', {
                defaultValue: 'Your current addon will be removed at the end of the billing cycle.',
              })}
            </Text.Normal>
          </Box>
        ) : selectedOneVariant ? (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'viridian1000',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={'viridian100'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {addon.title}
              </Text.Normal>
            </Box>
            {showPrice && (
              <PriceInfo
                variant={addon.getSpecificVariant(selectedOneVariant)}
                period={period}
                currency={currency}
                seats={seats}
                currentSeats={currentSeats}
                discount={discount}
                disabled={false}
              />
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default OneVariantSelector
